import React, { useState } from 'react';
import { useLexicon } from '../../../hooks/useLexicon';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import shortAnswerIcon from '../../../static/icons/ShortAnsIcon.svg';
import CommonPopup from '../../TheoryQuestionFrame/CommonPopup';
import eyeIcon from '../../../static/icons/Eye.svg';
import deleteIcon from '../../../static/img/deletered.svg';
import arrowDownIcon from '../../../static/img/arrowdown2.svg';
import QuestionTooltip from '../../../components2/QuestionTooltip';
import questionMark from '../../../static/img/question-mark.svg';

export const ShortAnswerFeedbackType = ({
  formData,
  updateFormData,
  allShortAnswers = [],
  onShortAnswerPreview,
  showLabel = true,
  showTooltip = true,
  filterSubject = null,
  hideGoToLink = false,
  placeholder = 'Select',
}) => {
  const { t } = useLexicon();
  const history = useHistory();
  const [openPopups, setOpenPopups] = useState({});

  const selectedIds = formData.shortAnswerIds || [];

  const handleSelect = (shortAnswer, index) => {
    const currentIds = [...selectedIds];
    if (index !== undefined) {
      currentIds[index] = shortAnswer.id;
    } else {
      if (!currentIds.includes(shortAnswer.id)) {
        currentIds.push(shortAnswer.id);
      }
    }
    // Auto-enable showMarks when short answer is selected
    const showMarksUpdate = !formData.showMarks ? {
      showMarks: true,
      marksOutOf: formData.marksOutOf || 20,
    } : {};
    updateFormData({
      shortAnswerIds: currentIds,
      ...showMarksUpdate,
    });
    setOpenPopups({});
  };

  const handleDelete = (index) => {
    const currentIds = [...selectedIds];
    currentIds.splice(index, 1);
    updateFormData({
      shortAnswerIds: currentIds,
    });
  };

  const handlePreview = (item) => {
    if (item && onShortAnswerPreview) {
      onShortAnswerPreview(item);
    }
  };

  const getItemById = (id) => {
    return allShortAnswers.find(sa => sa.id === id);
  };

  const availableItems = allShortAnswers.filter(sa => !selectedIds.includes(sa.id));
  
  const getFilteredItemsForEdit = (itemId) => {
    return allShortAnswers.filter(sa =>
      !selectedIds.includes(sa.id) || sa.id === itemId
    );
  };

  return (
    <Container>
      {showLabel && (
        <InputHeader>
          <OptionIcon>
            <IconImage src={shortAnswerIcon} alt="Short Answers" />
          </OptionIcon>
          <OptionContent>
            <OptionLabel>
              {t('Short Answer Marking Criteria')}
              {showTooltip && (
                <QuestionTooltip
                  text={t('Learn more about Short Answer Marking Criteria')}
                  img={questionMark}
                />
              )}
            </OptionLabel>
          </OptionContent>
        </InputHeader>
      )}

      <InputContent showLabel={showLabel}>
        {selectedIds.map((itemId, index) => {
          const currentItem = getItemById(itemId);
          if (!currentItem) return null;

          return (
            <SelectionItem key={`${itemId}-${index}`}>
              <SelectionRow>
                <SelectWrapper>
                  <SelectButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenPopups({ [index]: true });
                    }}
                  >
                    <span>{currentItem.title || currentItem.name || placeholder}</span>
                    <SelectArrow src={arrowDownIcon} alt="dropdown" />
                  </SelectButton>
                </SelectWrapper>
                <PreviewButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(currentItem);
                  }}
                >
                  <PreviewIcon src={eyeIcon} alt="preview" />
                </PreviewButton>
              </SelectionRow>
              <DeleteLink
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(index);
                }}
              >
                <DeleteIcon src={deleteIcon} alt="delete" />
                Delete
              </DeleteLink>

              {openPopups[index] && (
                <CommonPopup
                  isOpen={true}
                  onClose={() => setOpenPopups({})}
                  allMarkingCriterias={getFilteredItemsForEdit(itemId)}
                  updateMarkingCriteria={(selectedItem) => {
                    handleSelect(selectedItem, index);
                  }}
                  itemType="Short Answers"
                  selectedItem={currentItem}
                  hideTypeFilter={true}
                  filterSubject={filterSubject}
                />
              )}
            </SelectionItem>
          );
        })}

        {selectedIds.length === 0 && (
          <SelectionItem>
            <SelectionRow>
              <SelectWrapper>
                <SelectButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPopups({ add: true });
                  }}
                >
                  <span>{placeholder}</span>
                  <SelectArrow src={arrowDownIcon} alt="dropdown" />
                </SelectButton>
              </SelectWrapper>
            </SelectionRow>

            {openPopups.add && (
              <CommonPopup
                isOpen={true}
                onClose={() => setOpenPopups({})}
                allMarkingCriterias={availableItems}
                updateMarkingCriteria={(selectedItem) => {
                  handleSelect(selectedItem);
                }}
                itemType="Short Answers"
                selectedItem={null}
                hideTypeFilter={true}
                filterSubject={filterSubject}
              />
            )}
          </SelectionItem>
        )}


        {!hideGoToLink && (
          <GoToLink
            onClick={(e) => {
              e.stopPropagation();
              history.push('/tools/shortanswers');
            }}
          >
            Go to short answers
          </GoToLink>
        )}
      </InputContent>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InputHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const OptionIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const IconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const OptionLabel = styled.span`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-left: ${props => props.showLabel ? '56px' : '0'};
`;

const SelectionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const SelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 10px 12px 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text);
  min-height: 42px;
  overflow: hidden;

  & > span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus {
    outline: none;
    border-color: #7200E0;
    box-shadow: 0 0 0 3px rgba(114, 0, 224, 0.1);
  }

  &:hover {
    border-color: #7200E0;
  }
`;

const SelectArrow = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: #7200E0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background-color: #5a00b3;
  }
`;

const PreviewIcon = styled.img`
  filter: brightness(0) invert(1);
  width: 18px;
  height: 18px;
`;

const DeleteLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: fit-content;

  &:hover {
    color: #dc2626;
  }
`;

const DeleteIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const GoToLink = styled.button`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  width: fit-content;

  &:hover {
    color: #5a00b3;
  }
`;
